import React from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { 
  CheckCircleIcon,
  UserGroupIcon
} from '../../../../components/ui/Icons';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardDescription, 
  CardContent, 
  Badge 
} from '../../../../components/ui';

const ApprovalTemplateSteps = ({ selectedRecord }) => {
  const { theme } = useTheme();

  if (!selectedRecord.steps || selectedRecord.steps.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center">
            <UserGroupIcon className="w-4 h-4 mr-2" />
            Template Steps
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className={cn("w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3", theme.colors.surface)}>
              <CheckCircleIcon className={cn("w-6 h-6", theme.text.secondary)} />
            </div>
            <h3 className={cn("text-sm font-medium mb-1", theme.text.primary)}>No Steps Defined</h3>
            <p className={cn("text-xs", theme.text.secondary)}>
              This template doesn't have any defined steps yet.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center">
          <CheckCircleIcon className="w-4 h-4 mr-2" />
          Template Steps
        </CardTitle>
        <CardDescription>
          {selectedRecord.steps.length} step{selectedRecord.steps.length !== 1 ? 's' : ''} configured
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {selectedRecord.steps.map((step, index) => (
            <div key={step.id || index} className={cn("p-4 rounded-lg border", theme.border.light, theme.colors.background)}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className={cn("w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200")}>
                    {step.stepNumber || index + 1}
                  </div>
                  <div>
                    <h4 className={cn("text-sm font-semibold", theme.text.primary)}>
                      {step.stepName || `Step ${step.stepNumber || index + 1}`}
                      {step.stepType && (
                        <Badge variant="outline" size="sm" className="ml-2">
                          {step.stepType === 'review' ? 'Review' : 'Approval'}
                        </Badge>
                      )}
                    </h4>
                    <p className={cn("text-xs", theme.text.secondary)}>
                      Step {step.stepNumber || index + 1} of {selectedRecord.steps.length}
                    </p>
                  </div>
                </div>
              </div>

              {/* Step Details */}
              <div className="space-y-2">
                <div className={cn("p-2 rounded", theme.colors.surface)}>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className={cn("text-xs font-medium", theme.text.secondary)}>Approver Type:</span>
                      <Badge variant="outline" size="sm">
                        {step.approverType?.charAt(0).toUpperCase() + step.approverType?.slice(1)}
                      </Badge>
                    </div>
                    {step.approver && (
                      <div className="flex items-center justify-between">
                        <span className={cn("text-xs font-medium", theme.text.secondary)}>Approver:</span>
                        <span className={cn("text-xs font-medium", theme.text.primary)}>
                          {step.approver.userDisplayName || 
                           `${step.approver.userFirstName || ''} ${step.approver.userLastName || ''}`.trim() || 
                           step.approver.userEmail || 'Unknown User'}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className={cn("text-xs font-medium", theme.text.secondary)}>Required:</span>
                      <Badge variant={step.isRequired !== false ? 'default' : 'outline'} size="sm">
                        {step.isRequired !== false ? 'Yes' : 'No'}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={cn("text-xs font-medium", theme.text.secondary)}>Can Delegate:</span>
                      <Badge variant={step.canDelegate ? 'default' : 'outline'} size="sm">
                        {step.canDelegate ? 'Yes' : 'No'}
                      </Badge>
                    </div>
                    {step.escalationDays && (
                      <div className="flex items-center justify-between">
                        <span className={cn("text-xs font-medium", theme.text.secondary)}>Escalation Days:</span>
                        <span className={cn("text-xs font-medium", theme.text.primary)}>
                          {step.escalationDays} day{step.escalationDays !== 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default ApprovalTemplateSteps;

